import { useState } from 'react'

import { api } from '~/api/client'
import { why } from '~/api/why'

import s from './FixArtist.module.css'

/**
 * Saying what an artist row actually is, from the act's own page.
 *
 * `/tidy` was built as a queue: the rows a rule caught, one at a time. That is the right
 * shape for working through a pile and the wrong one for the row you happen to be looking
 * at — `Secret Guest b2b Special Guest` is two placeholders and a separator, and no rule
 * caught it, so the only way to reach it was to already know it was wrong.
 *
 * The verdicts are the queue's own. `tidy.decide` never asked whether a row was queued, so
 * there is no new server behind this: the same four gestures, offered where you find them
 * rather than where a rule sent you.
 *
 * `undo` is deliberately not offered here. It belongs to the queue, where a wrong press
 * happens because you were reading the next row; a decision made on the act's own page is
 * one you went looking for.
 */
export function FixArtist({
  artistId,
  name,
  onDone,
}: {
  artistId: number
  name: string
  onDone(artistId: number): void
}) {
  const [called, setCalled] = useState(name)
  const [tail, setTail] = useState('')
  const [lines, setLines] = useState(name)
  /*
   * Which control was pressed, while its answer is out. Every control waits for it, because
   * where this page goes next is the answer: a rename can fold this row into another and this
   * id stops existing, so there is no next state to draw before the server says which. The
   * pressed one says so on itself (`aria-busy`), at the press.
   */
  const [pressed, setPressed] = useState<string | null>(null)
  const busy = pressed !== null
  const [failed, setFailed] = useState<string | null>(null)

  const pieces = lines
    .split('\n')
    .map(one => one.trim())
    .filter(Boolean)

  const decide = (
    control: string,
    verdict: 'qualifier' | 'split' | 'placeholder' | 'neither',
    said?: string,
    note?: string,
  ) => {
    setPressed(control)
    setFailed(null)
    api
      .tidy(artistId, verdict, said, note)
      // The id the bills ended up on, which is not always the one sent: a rename onto a name
      // that already exists folds this row into that one and this id stops existing.
      .then(answer => onDone(answer.artist))
      .catch((error: unknown) => setFailed(why(error)))
      .finally(() => setPressed(null))
  }
  const working = (control: string) => (pressed === control ? true : undefined)

  return (
    <div className={s.fix}>
      {/* The two boxes the queue uses, and the same division of labour: the name is what the
          act is called, the tail is what was true of that booking rather than of them. */}
      <div className={s.boxes}>
        <input
          className={s.box}
          value={called}
          aria-label="The act's name"
          onChange={e => setCalled(e.target.value)}
        />
        <input
          className={`${s.box} ${s.tail}`}
          value={tail}
          aria-label="What kind of set"
          placeholder="LIVE, DJ SET, a label…"
          onChange={e => setTail(e.target.value)}
        />
      </div>
      <button
        type="button"
        className={s.act}
        disabled={busy || !called.trim() || (called.trim() === name && !tail.trim())}
        aria-busy={working('rename')}
        onClick={() => decide('rename', 'qualifier', called.trim(), tail.trim())}
      >
        Rename
      </button>

      {/* One act per line, because deleting a whole line is one gesture where deleting part
          of a comma-separated string is three. */}
      <textarea
        className={s.lines}
        value={lines}
        rows={Math.min(Math.max(pieces.length, 1) + 1, 6)}
        aria-label="One act per line"
        onChange={e => setLines(e.target.value)}
      />
      <button
        type="button"
        className={s.act}
        disabled={busy || pieces.length < 2}
        aria-busy={working('split')}
        onClick={() => decide('split', 'split', pieces.join('\n'), tail.trim())}
      >
        {/* `Tidy` counts this correctly and this did not, so the button on an artist page
            read "Split into 1 acts" — which is also the state where it is disabled, so the
            one time a reader had to read it closely it was ungrammatical. */}
        Split into {pieces.length} {pieces.length === 1 ? 'act' : 'acts'}
      </button>

      <div className={s.ends}>
        <button
          type="button"
          className={s.act}
          disabled={busy}
          aria-busy={working('placeholder')}
          onClick={() =>
            decide('placeholder', 'placeholder', called.trim() === name ? '' : called.trim(), tail.trim())
          }
        >
          Somebody plays, we don’t know who
        </button>
        <button
          type="button"
          className={`${s.act} ${s.off}`}
          disabled={busy}
          aria-busy={working('neither')}
          onClick={() => decide('neither', 'neither')}
        >
          Nobody plays — take it off
        </button>
      </div>
      {failed ? <p className={s.trouble}>{failed}</p> : null}
    </div>
  )
}
